import { UI, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';

import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { SignedInRootStackParamList } from '../../../../../../routes/types';
import { customerDetailPrivatePensionActions } from '../../../../../../store/customerDetail/privatePension/actions';

import { PrivatePensionApplicationForm } from './components/PrivatePensionApplicationForm';
import { PrivatePensionApplicationData } from './components/PrivatePensionApplicationForm/constants';
import { PrivatePensionApplicationSuccess } from './components/PrivatePensionApplicationSuccess';
import { PrivatePensionInfo } from './components/PrivatePensionInfo';
import { PrivatePensionNewApplicationHeader } from './components/PrivatePensionNewApplicationHeader';
import { defaultError, PrivatePensionNewApplicationModalSteps } from './constants';

export const PrivatePensionNewApplicationModal: React.FC = () => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [step, setStep] = useState(PrivatePensionNewApplicationModalSteps.APPLICATION_FORM);
  const [infos, setInfos] = useState<PrivatePensionApplicationData>({
    advisorEmail: '',
    clientEmail: '',
    value: 0,
  });

  const { params } =
    useRoute<RouteProp<SignedInRootStackParamList, 'PrivatePensionNewApplicationModal'>>();
  const navigation = useNavigation();
  const { showToast, showToastFromError } = useToastCreator('private-pension-application');

  const { executePrivatePensionNewApplication } = customerDetailPrivatePensionActions();

  const responsiveWidth = useBreakpointValue({
    base: 1,
    sm: 1,
    md: 0.42,
  });

  const handleConfirmApplication = useCallback(
    ({ advisorEmail, clientEmail, value }: PrivatePensionApplicationData) => {
      setInfos({ advisorEmail, clientEmail, value });
      setIsExecuting(true);
      executePrivatePensionNewApplication(
        value,
        params.privatePension.insuranceProposalId,
        clientEmail,
        advisorEmail
      )
        .then(() => setStep(PrivatePensionNewApplicationModalSteps.SUCCESS))
        .catch((executeError) => {
          if (instanceOfErrorListResponse(executeError)) {
            (executeError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
              showToastFromError(errorItem)
            );
            return;
          }
          showToast(...defaultError);
        })
        .finally(() => setIsExecuting(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePrivatePensionNewApplication, params.privatePension.insuranceProposalId]
  );

  const renderStep = () => {
    switch (step) {
      case PrivatePensionNewApplicationModalSteps.APPLICATION_FORM:
        return (
          <VStack p={Spacing['inset.sm']} space={Spacing['stack.xxxs']}>
            <PrivatePensionNewApplicationHeader onClose={goBack} />
            <PrivatePensionInfo privatePensionContracted={params.privatePension} />
            <PrivatePensionApplicationForm
              handleReturn={goBack}
              isExecuting={isExecuting}
              handleConfirmApplication={handleConfirmApplication}
            />
          </VStack>
        );

      case PrivatePensionNewApplicationModalSteps.SUCCESS:
        return (
          <VStack p={Spacing['inset.sm']} space={Spacing['stack.xxxs']}>
            <PrivatePensionApplicationSuccess onClose={goBack} infos={infos} />
          </VStack>
        );

      default:
        return null;
    }
  };

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  return (
    <UI.SliderModal width={responsiveWidth} onDismiss={goBack}>
      <ScrollView>{renderStep()}</ScrollView>
    </UI.SliderModal>
  );
};
